/**
 * Modo História: a campanha, os capítulos e as cenas.
 *
 * A escaramuça é uma batalha solta. Um capítulo é a mesma batalha com o que vem
 * antes e o que vem depois: quem são aquelas pessoas, por que estão ali, e o
 * que muda quando a poeira baixa. É essa moldura que esta tela entrega — e o
 * mapa em si continua sendo jogado pela escaramuça de sempre.
 *
 * O progresso nunca é decidido aqui. A tela conta ao servidor que a batalha
 * terminou e como; quanto isso vale em Emblemas e se o próximo capítulo abre é
 * resposta dele.
 */

import { useEffect, useState } from 'react';
import { api, messageOf } from '../api/client.js';
import { ASSETS_BASE } from '../assets/manifest.js';
import { useAuth } from '../auth/AuthContext.js';
import { latestChapterSave, saveWhen, type SaveGame } from '../game/saves.js';
import { objectiveLabel, type ChapterRecruit, type RewardItem, type Scene } from '../game/story.js';
import { loadConfirmButton, useGamepad } from '../game/useGamepad.js';
import type { MapObjectiveData } from '../render/tilemap.js';

export type { Scene };

export interface Chapter {
  readonly id: number;
  readonly ordinal: number;
  readonly title: string;
  readonly mapId: number;
  readonly objective: MapObjectiveData | null;
  readonly rewardCoins: number;
  readonly rewardItems?: readonly RewardItem[];
  readonly unlockCharacters?: readonly number[];
  readonly allowedCharacters?: readonly number[] | null;
  readonly enemyLevel?: number;
  readonly difficulty: 'facil' | 'normal' | 'dificil';
  readonly status: string | null;
  readonly unlocked: boolean;
  readonly scenes: readonly Scene[];
  /** Os inimigos que mudam de lado se falarem com eles. Ver `ChapterRecruit`. */
  readonly recruits?: readonly ChapterRecruit[];
  /**
   * De que campanha este capítulo é.
   *
   * A API devolve os capítulos DENTRO da campanha e não repete o apelido dela
   * em cada um; quem carrega a lista carimba aqui. É por ele que a batalha
   * sabe onde procurar o capítulo seguinte quando o jogador vence.
   */
  readonly campaignSlug?: string;
}

interface CampaignSummary {
  readonly id: number;
  readonly slug: string;
  readonly title: string;
  readonly summary: string | null;
  readonly coverImage: string | null;
  readonly status: string;
  readonly chapters: number;
  readonly completed: number;
}

export function CampaignPage({
  onPlayChapter,
  onOpenAuth,
}: {
  /**
   * Manda o capítulo para a escaramuça, que é quem sabe jogar um mapa.
   *
   * Com `resume`, manda junto a batalha guardada: é a diferença entre começar
   * o capítulo e voltar ao turno em que ele parou.
   */
  readonly onPlayChapter: (chapter: Chapter, campaignSlug: string, resume?: SaveGame) => void;
  readonly onOpenAuth: () => void;
}): JSX.Element {
  const { user } = useAuth();
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmButton] = useState(loadConfirmButton);

  useEffect(() => {
    api
      .get<{ campaigns: CampaignSummary[] }>('/api/historia/campanhas')
      .then((data) => setCampaigns(data.campaigns))
      .catch((e) => setError(messageOf(e, 'Não consegui carregar as campanhas.')))
      .finally(() => setLoading(false));
  }, []);

  const open = async (slug: string) => {
    setError('');
    try {
      const data = await api.get<{ chapters: Chapter[] }>(
        `/api/historia/campanhas/${encodeURIComponent(slug)}`,
      );
      // O apelido da campanha é carimbado em cada capítulo aqui: a API o
      // devolve uma vez só, do lado de fora da lista, e quem joga o capítulo
      // precisa dele para achar o seguinte.
      setChapters(data.chapters.map((c) => ({ ...c, campaignSlug: slug })));
      setOpenSlug(slug);
    } catch (e) {
      setError(messageOf(e, 'Não consegui abrir a campanha.'));
    }
  };

  /*
   * Começar o capítulo é ir para a batalha — a abertura passa lá.
   *
   * Ela passava AQUI, sobre a lista de capítulos, com o fundo declarado na cena
   * cobrindo a tela: a conversa que antecede a batalha acontecia num lugar que
   * não era o mapa dela. Quem sabe mostrar o mapa é a batalha, e é lá que a
   * abertura passa agora, por cima do tabuleiro já montado.
   */
  const start = (chapter: Chapter) => {
    onPlayChapter(chapter, openSlug ?? '');
  };

  /*
   * Retomar pula a abertura: quem está voltando ao turno sete já a viu.
   */
  const resume = (chapter: Chapter, save: SaveGame) => {
    onPlayChapter(chapter, openSlug ?? '', save);
  };

  /*
   * O modo história inteiro no controle.
   *
   * Escolher a campanha, escolher o capítulo e começar eram só clique e
   * teclado: quem joga de joystick chegava até aqui pelo controle e tinha de
   * largá-lo justamente na hora de começar. O cursor é o mesmo em qualquer
   * uma das duas listas — qual delas está na tela é `openSlug` que diz.
   */
  const [cursor, setCursor] = useState(0);
  useEffect(() => {
    setCursor(0);
  }, [openSlug]);

  const list: readonly unknown[] = openSlug ? chapters : campaigns;
  const listSize = list.length;

  useGamepad(
    {
      onDirection: (_dx, dy) => {
        if (dy === 0 || listSize === 0) return;
        setCursor((c) => (c + (dy > 0 ? 1 : -1) + listSize) % listSize);
      },
      onConfirm: () => {
        if (openSlug) {
          const chapter = chapters[cursor];
          if (chapter?.unlocked) start(chapter);
        } else {
          const campaign = campaigns[cursor];
          if (campaign) void open(campaign.slug);
        }
      },
      onCancel: () => {
        if (openSlug) setOpenSlug(null);
      },
    },
    true,
    confirmButton,
  );

  if (loading) {
    return <section data-panel="historia"><p className="muted">carregando campanhas…</p></section>;
  }

  if (openSlug) {
    const campaign = campaigns.find((c) => c.slug === openSlug);

    return (
      <section data-panel="historia" className="campaign-detail">
        <button type="button" className="campaign-back" onClick={() => setOpenSlug(null)}>
          ← todas as campanhas
        </button>
        <h2>{campaign?.title ?? 'Campanha'}</h2>
        {campaign?.summary && <p className="campaign-summary">{campaign.summary}</p>}

        {error && <p className="importer-error">⚠️ {error}</p>}

        <ol className="chapter-list">
          {chapters.map((chapter, i) => (
            <li
              key={chapter.id}
              className={`chapter-card ${chapter.unlocked ? '' : 'locked'} ${chapter.status === 'concluido' ? 'done' : ''} ${i === cursor ? 'is-active' : ''}`}
              onMouseEnter={() => setCursor(i)}
            >
              <div className="chapter-ordinal">{chapter.ordinal}</div>
              <div className="chapter-body">
                <h3>{chapter.title}</h3>
                <p className="chapter-meta">
                  Objetivo: {objectiveLabel(chapter.objective)} · Mapa #{chapter.mapId} · {chapter.difficulty} · Inimigos Nível {chapter.enemyLevel ?? 1}
                  {chapter.rewardCoins > 0 && <> · ◈ {chapter.rewardCoins}</>}
                  {chapter.scenes.length > 0 && <> · {chapter.scenes.length} cena(s)</>}
                </p>
                {((chapter.unlockCharacters && chapter.unlockCharacters.length > 0) || (chapter.rewardItems && chapter.rewardItems.length > 0)) && (
                  <p className="chapter-rewards-preview">
                    🎁 <strong>Recompensas:</strong>{' '}
                    {chapter.unlockCharacters && chapter.unlockCharacters.length > 0 && (
                      <span className="reward-tag recruit-tag">✨ Novo Recruta</span>
                    )}
                    {chapter.rewardItems && chapter.rewardItems.length > 0 && (
                      <span className="reward-tag item-tag">
                        📦 {chapter.rewardItems.length} novo(s) item(ns)
                      </span>
                    )}
                  </p>
                )}
                {chapter.status === 'concluido' && <span className="chapter-badge">✅ concluído</span>}
              </div>
              <div className="chapter-actions">
                {(() => {
                  // Uma batalha guardada deste capítulo, se houver: o botão de
                  // retomar só aparece quando há para onde voltar.
                  const saved = openSlug ? latestChapterSave(openSlug, chapter.id) : null;
                  if (!saved) return null;
                  return (
                    <button
                      type="button"
                      className="chapter-play chapter-resume"
                      title={`Voltar ao turno ${saved.save.turn} (guardado ${saveWhen(saved.save)})`}
                      onClick={() => resume(chapter, saved.save)}
                    >
                      ⏵ Continuar (turno {saved.save.turn})
                    </button>
                  );
                })()}
                <button
                  type="button"
                  className="chapter-play"
                  disabled={!chapter.unlocked}
                  title={chapter.unlocked ? undefined : 'Termine o capítulo anterior para abrir este'}
                  onClick={() => start(chapter)}
                >
                  {chapter.status === 'concluido' ? '↺ Rejogar' : '▶ Jogar'}
                </button>
              </div>
            </li>
          ))}
        </ol>

        {chapters.length === 0 && (
          <p className="muted">Esta campanha ainda não tem capítulos.</p>
        )}
      </section>
    );
  }

  return (
    <section data-panel="historia">
      <h2>📖 Modo História</h2>
      <p className="muted">
        Campanhas com capítulos, diálogos e cenas. Vença um capítulo para abrir o próximo e
        ganhar Emblemas.
      </p>

      {user === null && (
        <p className="campaign-hint">
          <button type="button" className="linklike" onClick={onOpenAuth}>Entre na sua conta</button>
          {' '}para guardar o seu progresso e receber as recompensas.
        </p>
      )}

      {error && <p className="importer-error">⚠️ {error}</p>}

      {campaigns.length === 0 ? (
        <p className="muted">
          Nenhuma campanha publicada ainda. Um administrador cria a primeira no Painel Admin →
          História.
        </p>
      ) : (
        <div className="campaign-grid">
          {campaigns.map((campaign, i) => (
            <article
              key={campaign.id}
              className={`campaign-card ${i === cursor ? 'is-active' : ''}`}
              onMouseEnter={() => setCursor(i)}
            >
              {campaign.coverImage && (
                <div
                  className="campaign-cover"
                  style={{ backgroundImage: `url(${ASSETS_BASE}/img/pictures/${campaign.coverImage}.webp)` }}
                />
              )}
              <div className="campaign-card-body">
                <h3>
                  {campaign.title}
                  {campaign.status !== 'publicada' && (
                    <span className="campaign-draft">{campaign.status}</span>
                  )}
                </h3>
                {campaign.summary && <p>{campaign.summary}</p>}
                <p className="campaign-progress">
                  {campaign.completed} de {campaign.chapters} capítulo(s)
                </p>
                <button type="button" onClick={() => void open(campaign.slug)}>
                  Abrir
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}
